/**
 * 프로그램 워크스페이스 Repository — 메모리 캐시(동기 읽기) + IndexedDB(비동기 영속).
 */

import {
  loadProgramWorkspaceIdbSnapshot,
  persistProgramWorkspaceIdbSnapshot,
  persistProgramWorkspaceScopePartial,
} from './programWorkspaceIdb';
import type {
  ProgramWorkspaceClipboard,
  ProgramWorkspaceScopeEntry,
  ProgramWorkspaceState,
} from './programWorkspaceTypes';

const emptyState = (): ProgramWorkspaceState => ({ scopes: {}, clipboard: null });

let memoryState: ProgramWorkspaceState = emptyState();
let hydrated = false;
let hydratePromise: Promise<void> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let persistInFlight: Promise<void> | null = null;
const dirtyScopeIdsForPersist = new Set<string>();
let clipboardDirty = false;

const PERSIST_DEBOUNCE_MS = 120;

export function isProgramWorkspaceRepositoryHydrated(): boolean {
  return hydrated;
}

export function getProgramWorkspaceMemoryState(): ProgramWorkspaceState {
  return memoryState;
}

export function replaceProgramWorkspaceMemoryState(next: ProgramWorkspaceState): void {
  memoryState = next;
}

/** 앱 시작 시 1회 — LocalStorage→IDB 이전 포함 */
export function ensureProgramWorkspaceHydrated(): Promise<void> {
  if (hydrated) return Promise.resolve();
  if (!hydratePromise) {
    hydratePromise = loadProgramWorkspaceIdbSnapshot()
      .then((snapshot) => {
        memoryState = snapshot;
        hydrated = true;
      })
      .catch(() => {
        memoryState = emptyState();
        hydrated = true;
      });
  }
  return hydratePromise;
}

function schedulePersist(scopeId?: string): void {
  if (scopeId) dirtyScopeIdsForPersist.add(scopeId);
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void flushProgramWorkspaceRepository();
  }, PERSIST_DEBOUNCE_MS);
}

export function markProgramWorkspaceClipboardDirty(): void {
  clipboardDirty = true;
  schedulePersist();
}

export function scheduleProgramWorkspaceScopePersist(scopeId: string): void {
  schedulePersist(scopeId);
}

export async function flushProgramWorkspaceRepository(): Promise<void> {
  if (persistInFlight) return persistInFlight;
  persistInFlight = (async () => {
    if (!hydrated) await ensureProgramWorkspaceHydrated();

    if (dirtyScopeIdsForPersist.size === 0 && !clipboardDirty) {
      return;
    }

    const scopeIds = Array.from(dirtyScopeIdsForPersist);
    dirtyScopeIdsForPersist.clear();
    const clip = clipboardDirty;
    clipboardDirty = false;

    if (scopeIds.length === 1 && !clip) {
      const scopeId = scopeIds[0];
      const entry = memoryState.scopes[scopeId] ?? null;
      await persistProgramWorkspaceScopePartial(scopeId, entry, memoryState.clipboard);
      return;
    }

    await persistProgramWorkspaceIdbSnapshot(memoryState);
  })().finally(() => {
    persistInFlight = null;
  });
  return persistInFlight;
}

export function registerProgramWorkspaceRepositoryPageHideFlush(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onPageHide = () => {
    void flushProgramWorkspaceRepository();
  };
  window.addEventListener('pagehide', onPageHide);
  return () => window.removeEventListener('pagehide', onPageHide);
}
